'use client';

import type { JobPortalOffer } from '@eliadmin/core/src/api/jobportal/offers-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import type { Course } from '@eliadmin/core/src/api/courses/coursers-api';
import type { CurriculumVitae } from '@eliadmin/core/src/api/curriculum-vitaes/curriculums-api';
import { useRef, useState } from 'react';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { AiOutlineLike } from 'react-icons/ai';

export const AddToFavourite = ({
  offer,
  curriculum,
  course,
  type,
  className,
}: {
  offer?: JobPortalOffer;
  curriculum?: CurriculumVitae;
  course?: Course & { is_favorite: boolean };
  type: 'offer' | 'curriculum' | 'course';
  className?: { container?: string; icon?: string };
}) => {
  const [isFavorite, setIsFavorite] = useState(
    type === 'offer'
      ? offer?.is_favorite
      : type === 'curriculum'
        ? curriculum?.is_favorite
        : (course?.is_favorite ?? false),
  );
  const [isLoading, setIsLoading] = useState(false);
  const pending = useRef(false);
  const [hasError, setHasError] = useState(false);

  const me = useCustomerStore(state => state.me);

  const handleSetFavorite = async () => {
    if (!me || pending.current) return;
    const slug =
      type === 'offer' ? offer?.slug : type === 'curriculum' ? curriculum?.slug : course?.slug;
    if (!slug) return;
    pending.current = true;
    setIsLoading(true);
    setHasError(false);
    try {
      const api = await import('@eliadmin/core/src/api/jobportal/offers-api');
      const action =
        type === 'offer'
          ? api.addFavoriteOffer
          : type === 'curriculum'
            ? api.addToFavoriteCurriculum
            : api.addToFavoriteCourse;
      await action({ authToken: me.authToken, slug });
      setIsFavorite(value => !value);
    } catch {
      setHasError(true);
    } finally {
      pending.current = false;
      setIsLoading(false);
    }
  };

  return (
    <Button
      type="button"
      aria-label={isFavorite ? 'Odstrániť z obľúbených' : 'Pridať medzi obľúbené'}
      aria-pressed={!!isFavorite}
      aria-busy={isLoading}
      disabled={isLoading}
      size={'icon-sm'}
      variant={'ghost'}
      className={cn(
        'ea-job-offer-card__favorite-contianer text-primary-foreground hover:bg-primary hover:text-white',
        'flex items-center justify-center rounded-full bg-white text-primary',
        className?.container,
        isFavorite && 'bg-primary text-white',
      )}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        handleSetFavorite();
      }}
    >
      {!isLoading && (
        <AiOutlineLike
          className={cn('ea-job-offer-card__favorite-icon', 'h-5 w-5', className?.icon)}
        />
      )}
      {isLoading && <Spinner className="h-4 w-4 text-white" />}
      {hasError && (
        <span role="alert" className="sr-only">
          Zmenu sa nepodarilo uložiť. Skúste znova.
        </span>
      )}
    </Button>
  );
};
